import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Users, Search, UserPlus, Trash2, LogOut, Edit, Monitor, Unlock, AlertTriangle, Eye, Building2, History } from 'lucide-react';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [courses, setCourses] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [searchPhone, setSearchPhone] = useState('');
  const [searchEmail, setSearchEmail] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterCourse, setFilterCourse] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('');
  const [showEnrollDialog, setShowEnrollDialog] = useState(false);
  const [showEditEnrollDialog, setShowEditEnrollDialog] = useState(false);
  const [showSessionsDialog, setShowSessionsDialog] = useState(false);
  const [selectedUserSessions, setSelectedUserSessions] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [editingEnrollment, setEditingEnrollment] = useState(null);
  const [enrollForm, setEnrollForm] = useState({
    user_id: '',
    course_id: '',
    months: 5
  });

  useEffect(() => {
    fetchUsers();
    fetchEnrollments();
    fetchCourses();
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    try {
      const response = await axios.get('/departments');
      setDepartments(response.data);
    } catch (error) {
      console.error('Failed to fetch departments:', error);
    }
  };

  const getDepartmentNames = (deptIds) => {
    if (!deptIds || deptIds.length === 0) return [];
    return deptIds.map(id => {
      const dept = departments.find(d => d.id === id);
      return dept?.name || 'Unknown';
    });
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/admin/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchEnrollments = async () => {
    try {
      const response = await axios.get('/admin/enrollments');
      setEnrollments(response.data);
    } catch (error) {
      console.error('Failed to fetch enrollments:', error);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await axios.get('/courses');
      setCourses(response.data);
    } catch (error) {
      console.error('Failed to fetch courses:', error);
    }
  };

  const handleSearch = async () => {
    try {
      const params = new URLSearchParams();
      if (searchPhone) params.append('phone', searchPhone);
      if (searchEmail) params.append('email', searchEmail);
      if (filterRole && filterRole !== 'all') params.append('role', filterRole);
      if (filterCourse && filterCourse !== 'all') params.append('course_id', filterCourse);
      
      const response = await axios.get(`/admin/users/search?${params.toString()}`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Search failed');
    }
  };

  const handleClearFilters = () => {
    setSearchPhone('');
    setSearchEmail('');
    setFilterRole('');
    setFilterCourse('');
    setFilterDepartment('');
    fetchUsers();
  };

  const handleChangeRole = async (userId, newRole) => {
    try {
      await axios.put(`/admin/users/${userId}`, { role: newRole });
      toast.success(`User role changed to ${newRole}`);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to change role');
    }
  };

  const handleClearSession = async (userId) => {
    try {
      await axios.post(`/admin/users/${userId}/revoke-sessions`);
      toast.success('All sessions revoked');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to revoke sessions');
    }
  };

  const handleDeleteSessionHistory = async (userId) => {
    if (!window.confirm('Delete all session history for this user? This cannot be undone.')) return;
    try {
      await axios.delete(`/admin/users/${userId}/sessions`);
      toast.success('Session history deleted');
      setShowSessionsDialog(false);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to delete session history');
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    const newStatus = !currentStatus;
    try {
      await axios.put(`/admin/users/${userId}/status`, { is_active: newStatus });
      toast.success(`User ${newStatus ? 'activated' : 'deactivated'}`);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Delete this user? This action cannot be undone.')) return;
    try {
      await axios.delete(`/admin/users/${userId}`);
      toast.success('User deleted');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const handleViewSessions = async (userId, showAll = false) => {
    try {
      const response = await axios.get(`/admin/users/${userId}/sessions?active_only=${!showAll}`);
      setSelectedUserSessions(response.data.sessions || []);
      setSelectedUserId(userId);
      setShowSessionsDialog(true);
    } catch (error) {
      toast.error('Failed to fetch sessions');
    }
  };

  const handleViewLoginHistory = async (userId, startDate = null, endDate = null) => {
    try {
      let url = `/admin/users/${userId}/login-history`;
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url);
      setSelectedUserSessions(response.data.login_history || []);
      setSelectedUserId(userId);
      setShowSessionsDialog(true);
    } catch (error) {
      toast.error('Failed to fetch login history');
    }
  };

  const handleRevokeFromDialog = async () => {
    if (!selectedUserId) return;
    try {
      await axios.post(`/admin/users/${selectedUserId}/revoke-sessions`);
      toast.success('All sessions revoked successfully');
      setShowSessionsDialog(false);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to revoke sessions');
    }
  };

  const handleUnlockAccount = async (userId) => {
    try {
      await axios.post(`/admin/users/${userId}/unlock`);
      toast.success('Account unlocked');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to unlock account');
    }
  };

  const handleManualEnroll = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/admin/enroll-user', enrollForm);
      toast.success('User enrolled successfully');
      setShowEnrollDialog(false);
      setEnrollForm({ user_id: '', course_id: '', months: 5 });
      fetchEnrollments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Enrollment failed');
    }
  };

  const handleEditEnrollment = (enrollment) => {
    setEditingEnrollment(enrollment);
    setShowEditEnrollDialog(true);
  };

  const handleUpdateEnrollment = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        expiry_date: editingEnrollment.expiry_date
      };
      await axios.put(`/admin/enrollments/${editingEnrollment.id}`, updateData);
      toast.success('Enrollment updated successfully');
      setShowEditEnrollDialog(false);
      setEditingEnrollment(null);
      fetchEnrollments();
    } catch (error) {
      toast.error('Failed to update enrollment');
    }
  };

  const handleDeleteEnrollment = async (enrollmentId) => {
    if (!window.confirm('Are you sure you want to delete this enrollment?')) return;
    
    try {
      await axios.delete(`/admin/enrollments/${enrollmentId}`);
      toast.success('Enrollment deleted successfully');
      fetchEnrollments();
    } catch (error) {
      toast.error('Failed to delete enrollment');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div className="min-w-0">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 truncate">User Management</h2>
          <p className="text-gray-600 text-sm sm:text-base">Manage users and their enrollments</p>
        </div>
        <Button onClick={() => setShowEnrollDialog(true)} className="bg-blue-600 hover:bg-blue-700 flex-shrink-0 text-xs sm:text-sm px-3 py-2">
          <UserPlus className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
          <span className="hidden xs:inline">Manual </span>Enrollment
        </Button>
      </div>

      <Tabs defaultValue="users" className="space-y-4">
        <TabsList>
          <TabsTrigger value="users">All Users</TabsTrigger>
          <TabsTrigger value="enrollments">Enrollments</TabsTrigger>
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-4">
          {/* Search & Filters */}
          <Card>
            <CardContent className="pt-6">
              <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Search Email</label>
                  <Input
                    value={searchEmail}
                    onChange={(e) => setSearchEmail(e.target.value)}
                    placeholder="Enter email"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Search Phone</label>
                  <Input
                    value={searchPhone}
                    onChange={(e) => setSearchPhone(e.target.value)}
                    placeholder="Enter phone number"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Filter by Role</label>
                  <Select value={filterRole || undefined} onValueChange={setFilterRole}>
                    <SelectTrigger>
                      <SelectValue placeholder="All roles" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Roles</SelectItem>
                      <SelectItem value="master_admin">Master Admin</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="student">Student</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Filter by Department</label>
                  <Select value={filterDepartment || undefined} onValueChange={setFilterDepartment}>
                    <SelectTrigger>
                      <SelectValue placeholder="All departments" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Departments</SelectItem>
                      {departments.map((dept) => (
                        <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Filter by Course</label>
                  <Select value={filterCourse || undefined} onValueChange={setFilterCourse}>
                    <SelectTrigger>
                      <SelectValue placeholder="All courses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Courses</SelectItem>
                      {courses.map((course) => (
                        <SelectItem key={course.id} value={course.id}>{course.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end gap-2">
                  <Button onClick={handleSearch} className="px-4 py-2 text-sm">
                    <Search className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                    <span className="hidden xs:inline">Search</span>
                    <span className="xs:hidden">Search</span>
                  </Button>
                  <Button onClick={handleClearFilters} variant="outline" className="px-3 py-2 text-sm">
                    <span className="hidden xs:inline">Clear</span>
                    <span className="xs:hidden">✕</span>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Users Table */}
          <Card>
            <CardHeader>
              <CardTitle>All Users ({users.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Auth Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Departments</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Joined</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {users
                      .filter(user => {
                        // Client-side department filter
                        if (filterDepartment && filterDepartment !== 'all') {
                          if (!user.departments || !user.departments.includes(filterDepartment)) {
                            return false;
                          }
                        }
                        return true;
                      })
                      .map((user) => {
                      const isLocked = user.lock_until && new Date(user.lock_until) > new Date();
                      return (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm">
                          <div className="font-medium">{user.display_name || user.email || user.phone}</div>
                          {user.name && user.email && (
                            <div className="text-xs text-gray-500">{user.email}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            user.auth_type === 'email' 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-orange-100 text-orange-700'
                          }`}>
                            {user.auth_type === 'email' ? 'Email' : 'Phone'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {user.departments && user.departments.length > 0 ? (
                            <div className="flex flex-wrap gap-1 max-w-48">
                              {getDepartmentNames(user.departments).map((name, idx) => (
                                <span 
                                  key={idx}
                                  className="px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded text-xs font-medium"
                                  title={name}
                                >
                                  {name.length > 15 ? name.substring(0, 15) + '...' : name}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-gray-400 text-xs flex items-center gap-1">
                              <Building2 className="w-3 h-3" />
                              None
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <Select
                            value={user.role}
                            onValueChange={(newRole) => handleChangeRole(user.id, newRole)}
                          >
                            <SelectTrigger className={`w-32 h-8 text-xs font-medium ${
                              user.role === 'master_admin' 
                                ? 'bg-red-50 text-red-700 border-red-200' 
                                : user.role === 'admin' 
                                  ? 'bg-purple-50 text-purple-700 border-purple-200' 
                                  : 'bg-blue-50 text-blue-700 border-blue-200'
                            }`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="master_admin" className="text-red-700">Master Admin</SelectItem>
                              <SelectItem value="admin" className="text-purple-700">Admin</SelectItem>
                              <SelectItem value="student" className="text-blue-700">Student</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={user.is_active !== false}
                                onCheckedChange={() => handleToggleUserStatus(user.id, user.is_active !== false)}
                                className="scale-75"
                              />
                              <span className={`text-xs ${user.is_active !== false ? 'text-green-600' : 'text-gray-400'}`}>
                                {user.is_active !== false ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                            {isLocked && (
                              <span className="text-red-600 text-xs flex items-center gap-1">
                                <AlertTriangle className="w-3 h-3" />
                                Locked
                              </span>
                            )}
                            {!user.is_verified && user.auth_type === 'email' && (
                              <span className="text-amber-600 text-xs">Unverified</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div className="flex gap-1 flex-wrap">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleViewSessions(user.id)}
                              title="View sessions"
                            >
                              <Eye className="w-3 h-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeleteSessionHistory(user.id)}
                              title="Delete session history"
                              className="text-orange-600 border-orange-300 hover:bg-orange-50"
                            >
                              <History className="w-3 h-3" />
                            </Button>
                            {isLocked && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleUnlockAccount(user.id)}
                                title="Unlock account"
                                className="text-amber-600 border-amber-300"
                              >
                                <Unlock className="w-3 h-3" />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleClearSession(user.id)}
                              title="Revoke all sessions"
                            >
                              <LogOut className="w-3 h-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeleteUser(user.id)}
                              title="Delete user"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )})}
                  </tbody>
                </table>
              </div>
              {users.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p>No users found</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Enrollments Tab */}
        <TabsContent value="enrollments">
          <Card>
            <CardHeader>
              <CardTitle>All Enrollments ({enrollments.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Course</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Purchase Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expiry Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {enrollments.map((enrollment) => {
                      const isExpired = new Date(enrollment.expiry_date) < new Date();
                      return (
                        <tr key={enrollment.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm">{enrollment.user_phone}</td>
                          <td className="px-4 py-3 text-sm">{enrollment.course_name}</td>
                          <td className="px-4 py-3 text-sm font-semibold">₹{enrollment.amount}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {new Date(enrollment.purchase_date).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {new Date(enrollment.expiry_date).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              isExpired 
                                ? 'bg-red-100 text-red-700' 
                                : 'bg-green-100 text-green-700'
                            }`}>
                              {isExpired ? 'Expired' : 'Active'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleEditEnrollment(enrollment)}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="destructive"
                                onClick={() => handleDeleteEnrollment(enrollment.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {enrollments.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <p>No enrollments found</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Manual Enrollment Dialog */}
      <Dialog open={showEnrollDialog} onOpenChange={setShowEnrollDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manual User Enrollment</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleManualEnroll} className="space-y-4">
            <div>
              <label className="text-sm font-medium">Select User</label>
              <Select value={enrollForm.user_id || undefined} onValueChange={(value) => setEnrollForm({ ...enrollForm, user_id: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose user" />
                </SelectTrigger>
                <SelectContent>
                  {users.filter(u => u.role === 'student').map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.display_name || user.email || user.phone}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Select Course</label>
              <Select value={enrollForm.course_id || undefined} onValueChange={(value) => setEnrollForm({ ...enrollForm, course_id: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose course" />
                </SelectTrigger>
                <SelectContent>
                  {courses.map((course) => (
                    <SelectItem key={course.id} value={course.id}>{course.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Validity (Months)</label>
              <Input
                type="number"
                value={enrollForm.months}
                onChange={(e) => setEnrollForm({ ...enrollForm, months: parseInt(e.target.value) })}
                min="1"
                max="12"
              />
            </div>
            <div className="flex gap-3 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowEnrollDialog(false)}>
                Cancel
              </Button>
              <Button type="submit">Enroll User</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Enrollment Dialog */}
      <Dialog open={showEditEnrollDialog} onOpenChange={setShowEditEnrollDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Enrollment</DialogTitle>
          </DialogHeader>
          {editingEnrollment && (
            <form onSubmit={handleUpdateEnrollment} className="space-y-4">
              <div>
                <label className="text-sm font-medium">User</label>
                <Input value={editingEnrollment.user_phone} disabled />
              </div>
              <div>
                <label className="text-sm font-medium">Course</label>
                <Input value={editingEnrollment.course_name} disabled />
              </div>
              <div>
                <label className="text-sm font-medium">Expiry Date</label>
                <Input
                  type="date"
                  value={editingEnrollment.expiry_date ? editingEnrollment.expiry_date.split('T')[0] : ''}
                  onChange={(e) => setEditingEnrollment({ 
                    ...editingEnrollment, 
                    expiry_date: e.target.value 
                  })}
                />
              </div>
              <div className="flex gap-3 justify-end">
                <Button type="button" variant="outline" onClick={() => setShowEditEnrollDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit">Update Enrollment</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Sessions Dialog */}
      <Dialog open={showSessionsDialog} onOpenChange={setShowSessionsDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Monitor className="w-5 h-5" />
              User Sessions
            </DialogTitle>
          </DialogHeader>
          
          {/* Filters */}
          <div className="flex flex-wrap gap-3 pb-3 border-b">
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleViewSessions(selectedUserId, false)}
              className="text-xs"
            >
              Active Only
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleViewLoginHistory(selectedUserId)}
              className="text-xs"
            >
              All Login History
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                const today = new Date().toISOString().split('T')[0];
                handleViewLoginHistory(selectedUserId, today, today);
              }}
              className="text-xs"
            >
              Today
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                const today = new Date();
                const weekAgo = new Date(today.setDate(today.getDate() - 7)).toISOString().split('T')[0];
                handleViewLoginHistory(selectedUserId, weekAgo);
              }}
              className="text-xs"
            >
              Last 7 Days
            </Button>
          </div>
          
          <div className="flex-1 overflow-y-auto space-y-3 py-3">
            {selectedUserSessions.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Monitor className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p>No sessions found</p>
              </div>
            ) : (
              selectedUserSessions.map((session) => (
                <div 
                  key={session.id} 
                  className={`p-4 rounded-lg border ${session.is_active ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}
                >
                  <div className="flex justify-between items-start">
                    <div className="space-y-1 flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${session.is_active ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                        <span className="font-medium text-sm">
                          {session.is_active ? 'Active' : 'Ended'}
                        </span>
                        {session.invalidated_reason && (
                          <span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded">
                            {session.invalidated_reason}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-600 max-w-md truncate" title={session.user_agent}>
                        {session.user_agent || 'Unknown Device'}
                      </p>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                        <span className="font-medium">IP: {session.ip_address || 'N/A'}</span>
                        <span>Login: {session.created_at ? new Date(session.created_at).toLocaleString() : 'N/A'}</span>
                        {session.is_active && (
                          <span>Last active: {session.last_used ? new Date(session.last_used).toLocaleString() : 'N/A'}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          
          <div className="flex gap-3 justify-between pt-3 border-t">
            <div className="text-xs text-gray-500">
              {selectedUserSessions.length} session(s) found
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowSessionsDialog(false)}>
                Close
              </Button>
              {selectedUserSessions.some(s => s.is_active) && (
                <Button 
                  variant="destructive" 
                  onClick={handleRevokeFromDialog}
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Terminate All Sessions
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;
